import type { User } from "firebase/auth";

/**
 * Admin Role Management Utilities
 * 
 * Uses Firebase custom claims to check user roles.
 * Custom claims must be set server-side using Firebase Admin SDK.
 * 
 * Role hierarchy:
 * - admin: Full access to all moderation features
 * - senior_moderator: Can handle Tier 3 content, escalate, ban users
 * - moderator: Can handle Tier 1 and Tier 2 content, approve/remove content
 * - null/undefined: Regular user, no moderation access
 */

export type UserRole = "admin" | "senior_moderator" | "moderator" | null;

/**
 * Get user role from Firebase custom claims
 * Note: This function is synchronous but custom claims are in the ID token.
 * Use getUserRoleAsync for accurate role checking.
 * @param user - Firebase User object
 * @returns User role or null if not set
 */
export const getUserRole = (user: User | null): UserRole => {
  if (!user) return null;
  
  // Custom claims are not directly available on the User object
  // They are in the ID token. This is a fallback that won't work reliably.
  // Use getUserRoleAsync instead.
  return null;
};

/**
 * Get user role from Firebase custom claims (async version)
 * Decodes the ID token to get custom claims
 * @param user - Firebase User object
 * @param forceRefresh - Whether to force token refresh
 * @returns User role or null if not set
 */
export const getUserRoleAsync = async (
  user: User | null,
  forceRefresh: boolean = false
): Promise<UserRole> => {
  if (!user) return null;
  
  try {
    // Get the ID token (with optional refresh)
    const token = await user.getIdToken(forceRefresh);
    
    // Decode the token to get custom claims
    // JWT tokens have 3 parts: header.payload.signature
    const payload = JSON.parse(atob(token.split('.')[1]));
    const role = payload.role;
    
    if (role === "admin" || role === "senior_moderator" || role === "moderator") {
      return role;
    }
    
    return null;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("Error getting user role:", error);
    return null;
  }
};

/**
 * Check if user is an admin (async version)
 * @param user - Firebase User object
 * @param forceRefresh - Whether to force token refresh
 * @returns Promise that resolves to true if user has admin role
 */
export const isAdminAsync = async (
  user: User | null,
  forceRefresh: boolean = false
): Promise<boolean> => {
  const role = await getUserRoleAsync(user, forceRefresh);
  return role === "admin";
};

/**
 * Check if user is a moderator (any level) (async version)
 * @param user - Firebase User object
 * @param forceRefresh - Whether to force token refresh
 * @returns Promise that resolves to true if user has moderator, senior_moderator, or admin role
 */
export const isModeratorAsync = async (
  user: User | null,
  forceRefresh: boolean = false
): Promise<boolean> => {
  const role = await getUserRoleAsync(user, forceRefresh);
  return role === "admin" || role === "senior_moderator" || role === "moderator";
};

/**
 * Check if user is a senior moderator or admin (async version)
 * @param user - Firebase User object
 * @param forceRefresh - Whether to force token refresh
 * @returns Promise that resolves to true if user has senior_moderator or admin role
 */
export const isSeniorModeratorAsync = async (
  user: User | null,
  forceRefresh: boolean = false
): Promise<boolean> => {
  const role = await getUserRoleAsync(user, forceRefresh);
  return role === "admin" || role === "senior_moderator";
};

/**
 * Check if user is an admin (synchronous, uses cached role)
 * @param user - Firebase User object
 * @returns true if user has admin role (may be stale if token not refreshed)
 */
export const isAdmin = (user: User | null): boolean => {
  // This won't work reliably - use isAdminAsync instead
  return false;
};

/**
 * Check if user is a moderator (any level) (synchronous, uses cached role)
 * @param user - Firebase User object
 * @returns true if user has moderator, senior_moderator, or admin role (may be stale)
 */
export const isModerator = (user: User | null): boolean => {
  // This won't work reliably - use isModeratorAsync instead
  return false;
};

/**
 * Check if user is a senior moderator or admin (synchronous, uses cached role)
 * @param user - Firebase User object
 * @returns true if user has senior_moderator or admin role (may be stale)
 */
export const isSeniorModerator = (user: User | null): boolean => {
  // This won't work reliably - use isSeniorModeratorAsync instead
  return false;
};

/**
 * Check if user can perform a specific moderation action
 * @param user - Firebase User object
 * @param actionType - Type of moderation action
 * @returns true if user has permission to perform the action
 */
export const canPerformAction = (
  user: User | null,
  actionType: "approve" | "remove" | "warn" | "suspend" | "ban" | "escalate"
): boolean => {
  const role = getUserRole(user);
  
  if (!role) return false;
  
  // All moderators can approve and remove
  if (actionType === "approve" || actionType === "remove") {
    return role === "admin" || role === "senior_moderator" || role === "moderator";
  }
  
  // Only senior moderators and admins can warn, suspend, ban, escalate
  if (actionType === "warn" || actionType === "suspend" || actionType === "ban" || actionType === "escalate") {
    return role === "admin" || role === "senior_moderator";
  }
  
  return false;
};

/**
 * Refresh user's ID token to get updated custom claims
 * This should be called after custom claims are updated server-side
 * @param user - Firebase User object
 * @returns Promise that resolves when token is refreshed
 */
export const refreshUserToken = async (user: User): Promise<void> => {
  await user.getIdToken(true); // Force refresh
};

